import type { MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  FlagIcon,
  HomeIcon,
  PencilSquareIcon,
  UserCircleIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../contexts/PermissionsContext';
import { fetchMyPerformanceInbox, MyPerformanceTask } from '../../services/peoplePerformanceApi';
import { buildSidebarDepartments } from './buildSidebarDepartments';
import { ACCOUNT_COMPANY_ICON } from './resolveModuleMenuIcon';
import { SidebarBrand } from './SidebarBrand';
import { SidebarCollapseToggle } from './SidebarCollapseToggle';
import { SidebarDepartmentGroup } from './SidebarDepartmentGroup';
import { SidebarNavBranch } from './SidebarNavBranch';
import { SidebarNavItem } from './SidebarNavItem';
import { flattenSidebarNavPaths, type SidebarNavLeaf } from './sidebarMenuTypes';
import { SidebarResizeHandle } from './SidebarResizeHandle';

type SidebarProps = {
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  collapsed?: boolean;
  desktopWidth?: number;
  isResizing?: boolean;
  onToggleCollapse?: () => void;
  onResizeStart?: (event: MouseEvent) => void;
};

function renderManualNavItem(
  item: SidebarNavLeaf,
  items: readonly SidebarNavLeaf[],
  collapsed: boolean,
  onNavigate?: () => void,
) {
  const siblingPaths = flattenSidebarNavPaths(items);
  if (item.children && item.children.length > 0) {
    return (
      <SidebarNavBranch
        key={item.code}
        item={item}
        siblingPaths={siblingPaths}
        collapsed={collapsed}
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <SidebarNavItem
      key={item.code}
      to={item.to}
      label={item.label}
      icon={item.icon}
      collapsed={collapsed}
      onNavigate={onNavigate}
      siblingPaths={siblingPaths}
    />
  );
}

const TASK_KIND_LABELS: Record<string, string> = {
  SelfEvaluation: 'Autoavaliação',
  ManagerEvaluation: 'Avaliar equipe',
  PeerReview: 'Avaliação 360',
  CycleFeedback: 'Feedback',
};

export function Sidebar({
  mobileOpen = false,
  onCloseMobile,
  collapsed = false,
  desktopWidth = 256,
  isResizing = false,
  onToggleCollapse,
  onResizeStart,
}: SidebarProps) {
  const { access, canAccessModule, isOrganizationAdmin } = usePermissions();
  const [performanceTasks, setPerformanceTasks] = useState<MyPerformanceTask[]>([]);

  const homePath =
    canAccessModule('dashboard.prospection') && !canAccessModule('dashboard.executive')
      ? '/dashboard/prospection'
      : '/dashboard';

  const departments = useMemo(
    () =>
      buildSidebarDepartments(
        access?.modules ?? [],
        access?.roleCodes ?? [],
        access?.isOwnerCustomer ?? false,
      ),
    [access?.modules, access?.roleCodes, access?.isOwnerCustomer],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const inbox = await fetchMyPerformanceInbox();
        if (!cancelled) {
          setPerformanceTasks(inbox.tasks);
        }
      } catch {
        if (!cancelled) {
          setPerformanceTasks([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [access?.userId]);

  function renderNavItems(isCollapsed: boolean, onNavigate?: () => void) {
    const mainDepartments = departments.filter((department) => department.id !== 'manual');
    const manualDepartment = departments.find((department) => department.id === 'manual');
    const canAccessGoalsMenu = canAccessModule('people.my-team') || isOrganizationAdmin;
    const accountSiblingPaths = [
      '/settings/profile',
      ...(canAccessModule('people.my-team') ? ['/people/my-team'] : []),
      ...(canAccessGoalsMenu ? ['/tasks/goals'] : []),
      '/tasks',
      '/tasks/inventory',
      '/performance',
      ...performanceTasks.map((task) => task.formPath),
      '/performance/notes',
      ...(isOrganizationAdmin ? ['/settings/company'] : []),
    ];

    return (
      <nav
        className={`sidebar-nav-scroll min-h-0 flex-1 space-y-1 overflow-y-auto py-4 ${
          isCollapsed ? 'px-2' : 'px-3'
        }`}
      >
        <SidebarNavItem
          to={homePath}
          label="Início"
          icon={HomeIcon}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
        />

        {mainDepartments.length > 0 ? <div className={isCollapsed ? 'pt-2' : 'pt-3'} /> : null}

        {mainDepartments.map((department) => (
          <SidebarDepartmentGroup
            key={department.id}
            department={department}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
          />
        ))}

        <div className={isCollapsed ? 'pt-2' : 'pt-3'} />
        {!isCollapsed ? (
          <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-primary-200">
            Conta
          </p>
        ) : null}
        <SidebarNavItem
          to="/settings/profile"
          label="Meu perfil"
          icon={UserCircleIcon}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
          siblingPaths={accountSiblingPaths}
        />
        {canAccessModule('people.my-team') ? (
          <SidebarNavItem
            to="/people/my-team"
            label="Minha Equipe"
            icon={UserGroupIcon}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
            siblingPaths={accountSiblingPaths}
          />
        ) : null}
        {canAccessGoalsMenu ? (
          <SidebarNavItem
            to="/tasks/goals"
            label="Metas e Marcos"
            icon={FlagIcon}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
            siblingPaths={accountSiblingPaths}
          />
        ) : null}
        <SidebarNavItem
          to="/tasks"
          label="Tarefas"
          icon={ClipboardDocumentListIcon}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
          siblingPaths={accountSiblingPaths}
        />
        <SidebarNavItem
          to="/performance"
          label="Desempenho"
          icon={ChartBarIcon}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
          siblingPaths={accountSiblingPaths}
        />
        {performanceTasks.map((task) => (
          <SidebarNavItem
            key={`${task.kind}-${task.evaluationId ?? task.peerReviewId ?? task.cycleId}-${task.formPath}`}
            to={task.formPath}
            label={
              task.kind === 'ManagerEvaluation' && task.subjectUserName
                ? `Avaliar ${task.subjectUserName.split(' ')[0]}`
                : TASK_KIND_LABELS[task.kind] ?? 'Formulário'
            }
            icon={ClipboardDocumentCheckIcon}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
            siblingPaths={accountSiblingPaths}
          />
        ))}
        <SidebarNavItem
          to="/performance/notes"
          label="Anotações"
          icon={PencilSquareIcon}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
          siblingPaths={accountSiblingPaths}
        />
        {!isOrganizationAdmin ? null : (
          <SidebarNavItem
            to="/settings/company"
            label="Empresa"
            icon={ACCOUNT_COMPANY_ICON}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
            siblingPaths={accountSiblingPaths}
          />
        )}
        {manualDepartment ? (
          <>
            <div className={isCollapsed ? 'pt-2' : 'pt-3'} />
            {manualDepartment.items.map((item) =>
              renderManualNavItem(item, manualDepartment.items, isCollapsed, onNavigate),
            )}
          </>
        ) : null}
      </nav>
    );
  }

  return (
    <>
      <aside
        className={`relative hidden h-full shrink-0 flex-col bg-primary-500 text-white lg:flex ${
          isResizing ? '' : 'transition-[width] duration-200 ease-out'
        }`}
        style={{ width: desktopWidth }}
      >
        <div className="shrink-0">
          <SidebarBrand collapsed={collapsed} />
        </div>
        {renderNavItems(collapsed)}
        {onToggleCollapse ? (
          <div className="shrink-0">
            <SidebarCollapseToggle collapsed={collapsed} onToggle={onToggleCollapse} />
          </div>
        ) : null}
        {!collapsed && onResizeStart ? <SidebarResizeHandle onResizeStart={onResizeStart} /> : null}
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-30 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Fechar menu"
            onClick={onCloseMobile}
          />
          <aside className="relative flex h-full w-72 max-w-[85vw] flex-col bg-primary-500 text-white shadow-xl">
            <button
              type="button"
              className="absolute right-3 top-3 z-10 rounded-xl p-2 hover:bg-primary-600"
              onClick={onCloseMobile}
              aria-label="Fechar"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div className="shrink-0">
              <SidebarBrand />
            </div>
            {renderNavItems(false, onCloseMobile)}
          </aside>
        </div>
      ) : null}
    </>
  );
}
